import { useCallback, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

// Minimum zoom we'll fly to when focusing a single selected pin. If the
// map is already closer we keep the current zoom (never zoom *out* to
// focus — that's disorienting).
const FOCUS_MIN_ZOOM = 14;

// TODO: migrate to self-hosted Protomaps before any high-volume tenant
// or HIPAA audit. Mapbox is the LYN-Rx-owned production provider,
// matching the driver app (@rnmapbox/maps) and the audit PDF Static
// Images API — one basemap style (streets-v12) across all surfaces.
const MAPBOX_STYLE_URL = 'mapbox://styles/mapbox/streets-v12';
// Fallback used only when VITE_MAPBOX_ACCESS_TOKEN is unset (dev
// oversight). Pure https style, no mapbox:// scheme, so mapbox-gl does
// not require a valid accessToken to render it.
const DEMO_STYLE_URL = 'https://demotiles.maplibre.org/style.json';

function resolveStyleUrl(): string {
  const key = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
  if (!key) {
    // eslint-disable-next-line no-console
    console.warn(
      '[RouteMap] VITE_MAPBOX_ACCESS_TOKEN not set; using low-fidelity demo tiles',
    );
    // Set to empty string to avoid mapbox-gl throwing on Map
    // construction when the style URL uses non-mapbox:// scheme.
    mapboxgl.accessToken = '';
    return DEMO_STYLE_URL;
  }
  mapboxgl.accessToken = key;
  return MAPBOX_STYLE_URL;
}

// Solo-stop fallback zoom. fitBounds collapses to a point when every
// stop shares one coordinate (or there is only one), so we manually
// setCenter + setZoom in that case.
const SOLO_STOP_ZOOM = 14;

// Multi-stop framing. Tight padding keeps pins near the frame edges (24
// clears the ~14px marker overhang + selected ring without clipping).
// maxZoom is a ceiling: it stops an ultra-tight cluster (all stops
// within a block) from zooming to z17+ and losing street context. It
// only ever zooms *out* relative to the all-pins fit, so it can never
// clip an outlier out of view — a spread route still frames every pin.
const FIT_PADDING = 24;
// Neighborhood-level cap. Bumped down from 15 (street-level) so tight
// routes don't lose regional context — a dispatcher glancing at the
// map should see the surrounding town, not just the block. Only kicks
// in when the natural fit would be tighter than this.
const FIT_MAX_ZOOM = 13;

// Minimum shape the map needs from a Stop. Kept structurally
// compatible with the route detail page's StopRow (numeric columns
// arrive from PostgREST as strings) so callers can pass the row
// through without remapping.
export type RouteMapStop = {
  id: string;
  stop_type: string;
  sequence_number: number | null;
  latitude: string | number | null;
  longitude: string | number | null;
  address?: string | null;
};

export type RouteMapProps = {
  stops: RouteMapStop[];
  className?: string;
  /** Currently-focused stop id, or null. Drives pin highlight + flyTo. */
  selectedStopId?: string | null;
  /** Called when a pin is clicked, with that pin's stop id (pin → card). */
  onSelectStop?: (id: string) => void;
};

type GeocodedStop = {
  id: string;
  stop_type: string;
  sequence_number: number | null;
  lat: number;
  lng: number;
  address: string | null;
};

function parseStops(stops: RouteMapStop[]): GeocodedStop[] {
  const out: GeocodedStop[] = [];
  for (const stop of stops) {
    if (stop.latitude === null || stop.longitude === null) {
      // eslint-disable-next-line no-console
      console.warn(`[RouteMap] skipping stop ${stop.id} — missing lat/lng`);
      continue;
    }
    const lat = typeof stop.latitude === 'string' ? Number(stop.latitude) : stop.latitude;
    const lng = typeof stop.longitude === 'string' ? Number(stop.longitude) : stop.longitude;
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      // eslint-disable-next-line no-console
      console.warn(`[RouteMap] skipping stop ${stop.id} — unparseable lat/lng`);
      continue;
    }
    out.push({
      id: stop.id,
      stop_type: stop.stop_type,
      sequence_number: stop.sequence_number,
      lat,
      lng,
      address: stop.address ?? null,
    });
  }
  return out;
}

// Pickup uses primary teal (#0C6169, matches the app's primary token);
// dropoffs are near-black. Box-shadow values are factored out so the
// selected state can swap to a ringed shadow and back.
const PICKUP_BG = '#0C6169';
const DROPOFF_BG = '#0A0A0A';
const BASE_SHADOW = '0 1px 3px rgba(0,0,0,0.3)';
// Teal focus ring + a deeper drop shadow for the selected pin.
const SELECTED_SHADOW = '0 0 0 4px rgba(12,97,105,0.35), 0 2px 6px rgba(0,0,0,0.4)';

// Inline styles (not tailwind classes) because the marker renders into
// Mapbox's overlay container, which sits outside React's
// tailwind-scanned tree — safer to be explicit. transform/transform-
// origin/transition live on the INNER circle: selection scales the
// inner element only. Mapbox owns the OUTER element's translate, so
// scaling the inner never fights the positioning transform.
function innerCircleCss(bg: string, fontWeight: number): string {
  return [
    'width: 28px',
    'height: 28px',
    'border-radius: 9999px',
    `background: ${bg}`,
    'color: #ffffff',
    'border: 2px solid #ffffff',
    `box-shadow: ${BASE_SHADOW}`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'font-family: Inter, system-ui, sans-serif',
    'font-size: 12px',
    `font-weight: ${fontWeight}`,
    'transform: scale(1)',
    'transform-origin: center',
    'transition: transform 120ms ease, box-shadow 120ms ease',
  ].join(';');
}

type MarkerEntry = {
  marker: mapboxgl.Marker;
  /** Element Mapbox positions (it owns this element's transform). */
  outer: HTMLElement;
  /** Styled circle we scale/restyle for the selected state. */
  inner: HTMLElement;
  lng: number;
  lat: number;
};

// Combined zoom (+/−) + home Mapbox control. One .mapboxgl-ctrl-
// group so all three buttons render as a single stacked cluster in the
// top-right — no visual gap between zoom and home like there would be
// with NavigationControl + a separate HomeControl. Zoom-in/out reuse
// Mapbox's own icon classes so they look native; home is a Feather-
// style SVG using currentColor.
class ZoomHomeControl implements mapboxgl.IControl {
  private _container: HTMLDivElement | null = null;
  private _homeBtn: HTMLButtonElement | null = null;
  private _zoomInBtn: HTMLButtonElement | null = null;
  private _zoomOutBtn: HTMLButtonElement | null = null;
  private _map: mapboxgl.Map | null = null;
  private readonly _onHome: () => void;

  constructor(onHome: () => void) {
    this._onHome = onHome;
  }

  onAdd(map: mapboxgl.Map): HTMLElement {
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

    // Zoom in — reuse Mapbox's own class so the built-in "+" icon
    // and hover/focus styling apply.
    const zoomIn = document.createElement('button');
    zoomIn.type = 'button';
    zoomIn.className = 'mapboxgl-ctrl-zoom-in';
    zoomIn.title = 'Zoom in';
    zoomIn.setAttribute('aria-label', 'Zoom in');
    zoomIn.innerHTML =
      '<span class="mapboxgl-ctrl-icon" aria-hidden="true"></span>';
    zoomIn.addEventListener('click', this._handleZoomIn);
    this._container.appendChild(zoomIn);
    this._zoomInBtn = zoomIn;

    // Zoom out — same class-reuse pattern for the built-in "−" icon.
    const zoomOut = document.createElement('button');
    zoomOut.type = 'button';
    zoomOut.className = 'mapboxgl-ctrl-zoom-out';
    zoomOut.title = 'Zoom out';
    zoomOut.setAttribute('aria-label', 'Zoom out');
    zoomOut.innerHTML =
      '<span class="mapboxgl-ctrl-icon" aria-hidden="true"></span>';
    zoomOut.addEventListener('click', this._handleZoomOut);
    this._container.appendChild(zoomOut);
    this._zoomOutBtn = zoomOut;

    // Home / fit-all — inline SVG so it inherits currentColor and sits
    // flush inside a Mapbox button (which is 29×29px with an inner
    // ~15px icon slot). Flex centering ensures visual alignment with
    // the +/− icons above it.
    const home = document.createElement('button');
    home.type = 'button';
    home.title = 'Fit all stops';
    home.setAttribute('aria-label', 'Fit all stops');
    home.style.cssText =
      'display:flex;align-items:center;justify-content:center;';
    home.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12l9-9 9 9"/><path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10"/></svg>';
    home.addEventListener('click', this._onHome);
    this._container.appendChild(home);
    this._homeBtn = home;

    return this._container;
  }

  onRemove(): void {
    this._zoomInBtn?.removeEventListener('click', this._handleZoomIn);
    this._zoomOutBtn?.removeEventListener('click', this._handleZoomOut);
    this._homeBtn?.removeEventListener('click', this._onHome);
    this._container?.parentNode?.removeChild(this._container);
    this._container = null;
    this._homeBtn = null;
    this._zoomInBtn = null;
    this._zoomOutBtn = null;
    this._map = null;
  }

  private _handleZoomIn = (): void => {
    this._map?.zoomIn();
  };

  private _handleZoomOut = (): void => {
    this._map?.zoomOut();
  };
}

// Outer wrapper (positioned by Mapbox) containing the styled circle.
// `interactive` is true when a click handler will be attached, which
// also flips the cursor to a pointer.
function buildMarkerEl(
  stop: GeocodedStop,
  interactive: boolean,
): { outer: HTMLElement; inner: HTMLElement } {
  const isPickup = stop.stop_type === 'pickup';
  const outer = document.createElement('div');
  outer.setAttribute('data-stop-id', stop.id);
  outer.setAttribute('data-stop-type', stop.stop_type);
  outer.setAttribute('data-selected', 'false');
  outer.style.cursor = interactive ? 'pointer' : 'default';

  const inner = document.createElement('div');
  inner.style.cssText = innerCircleCss(
    isPickup ? PICKUP_BG : DROPOFF_BG,
    isPickup ? 700 : 600,
  );
  inner.textContent = isPickup
    ? 'P'
    : stop.sequence_number === null
      ? '?'
      : String(stop.sequence_number);
  outer.appendChild(inner);
  return { outer, inner };
}

// Toggle a single marker's selected styling. Idempotent — safe to call
// on every marker when applying a new selection.
function setMarkerSelected(entry: MarkerEntry, selected: boolean): void {
  entry.outer.setAttribute('data-selected', selected ? 'true' : 'false');
  entry.inner.style.transform = selected ? 'scale(1.3)' : 'scale(1)';
  entry.inner.style.boxShadow = selected ? SELECTED_SHADOW : BASE_SHADOW;
  // Selected pin floats above its neighbors so the ring isn't clipped.
  entry.outer.style.zIndex = selected ? '10' : '';
}

export function RouteMap({
  stops,
  className,
  selectedStopId,
  onSelectStop,
}: RouteMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // stop id → placed marker. Populated in the 'load' handler; read by
  // the selection effect so a card click can restyle/focus one pin
  // without rebuilding the map.
  const markersRef = useRef<Map<string, MarkerEntry>>(new Map());
  const loadedRef = useRef(false);
  // Mirror the latest props into refs so the build effect (which only
  // re-runs on `stops`) can read the current selection / callback from
  // inside its async 'load' callback without going stale or being
  // forced to re-run on every selection change.
  const selectedIdRef = useRef<string | null | undefined>(selectedStopId);
  const onSelectRef = useRef<((id: string) => void) | undefined>(onSelectStop);

  useEffect(() => {
    selectedIdRef.current = selectedStopId;
  }, [selectedStopId]);
  useEffect(() => {
    onSelectRef.current = onSelectStop;
  }, [onSelectStop]);

  // Apply a selection to already-placed markers: restyle all pins, then
  // fly to the selected one only if it's outside the current viewport.
  // Reads refs only, so its identity is stable across renders.
  const applySelection = useCallback(
    (id: string | null | undefined): void => {
      const map = mapRef.current;
      const entries = markersRef.current;
      let selectedEntry: MarkerEntry | undefined;
      entries.forEach((entry, entryId) => {
        const isSelected = id != null && entryId === id;
        setMarkerSelected(entry, isSelected);
        if (isSelected) selectedEntry = entry;
      });
      if (!map || !selectedEntry) return;
      // Always recenter on click — the CNC pattern. The prior "only fly
      // when not in view" check was a false economy: pins hidden behind
      // the top-left overlay panel are technically inside getBounds()
      // but visually obscured, so they'd never fly-to. Centering every
      // click also matches the dispatcher's mental model ("I clicked,
      // something moved").
      map.flyTo({
        center: [selectedEntry.lng, selectedEntry.lat],
        zoom: Math.max(map.getZoom(), FOCUS_MIN_ZOOM),
        duration: 400,
      });
    },
    [],
  );

  // Effect A — build map + markers. Re-runs only when `stops` changes,
  // so a selection change never tears the map down.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const map = new mapboxgl.Map({
      container,
      style: resolveStyleUrl(),
      center: [0, 0],
      zoom: 1,
    });
    mapRef.current = map;
    const entries = markersRef.current;

    // Frame the viewport to include every stop. Shared by the initial
    // 'load' handler (duration 0 — snap) and the Home control (duration
    // 500 — smooth animate back from wherever the dispatcher zoomed to).
    // Handles two degenerate cases fitBounds can't: a single stop, and
    // all stops sharing one coord (fitBounds collapses to a zero-area
    // box). In both cases we setCenter + setZoom to the point manually.
    const frameStops = (duration: number): void => {
      const geocoded = parseStops(stops);
      const first = geocoded[0];
      if (!first) return;
      const allSame = geocoded.every(
        (s) => s.lat === first.lat && s.lng === first.lng,
      );
      if (geocoded.length === 1 || allSame) {
        map.setCenter([first.lng, first.lat]);
        map.setZoom(SOLO_STOP_ZOOM);
      } else {
        const bounds = new mapboxgl.LngLatBounds();
        for (const stop of geocoded) {
          bounds.extend([stop.lng, stop.lat]);
        }
        map.fitBounds(bounds, {
          padding: FIT_PADDING,
          maxZoom: FIT_MAX_ZOOM,
          duration,
        });
      }
    };

    // Combined zoom + home in a single top-right cluster. Compass
    // dropped — the map never rotates. Mapbox owns the control DOM
    // and cleans up on map.remove() automatically.
    map.addControl(
      new ZoomHomeControl(() => frameStops(500)),
      'top-right',
    );

    const placeMarkers = (): void => {
      const geocoded = parseStops(stops);
      if (geocoded.length > 0) {
        const interactive = onSelectRef.current != null;
        for (const stop of geocoded) {
          const { outer, inner } = buildMarkerEl(stop, interactive);
          if (interactive) {
            outer.addEventListener('click', () => {
              onSelectRef.current?.(stop.id);
            });
          }
          const marker = new mapboxgl.Marker({
            element: outer,
            anchor: 'center',
          })
            .setLngLat([stop.lng, stop.lat])
            .addTo(map);
          entries.set(stop.id, {
            marker,
            outer,
            inner,
            lng: stop.lng,
            lat: stop.lat,
          });
        }

        // Initial framing — snap (0ms) so the map isn't animating on
        // first paint.
        frameStops(0);
      }

      // Markers exist now — mark loaded and paint the current selection
      // (one may have arrived from a card click before 'load' fired).
      loadedRef.current = true;
      applySelection(selectedIdRef.current);
    };

    map.on('load', placeMarkers);

    return () => {
      entries.forEach((entry) => entry.marker.remove());
      entries.clear();
      map.remove();
      mapRef.current = null;
      loadedRef.current = false;
    };
  }, [stops, applySelection]);

  // Effect B — restyle + focus on selection change. No rebuild. Skips
  // until markers exist; the build effect paints the initial selection.
  useEffect(() => {
    if (!loadedRef.current) return;
    applySelection(selectedStopId);
  }, [selectedStopId, applySelection]);

  return (
    <div
      ref={containerRef}
      className={className ?? 'h-full w-full'}
      data-testid="route-map"
      aria-label="Map of route stops"
      role="region"
    />
  );
}
